import React from 'react';
import { Form, Formik } from 'formik';
import { handleUpdate, handleUpdateEmbed } from '../formHandlers';
import { getValidationSchema } from '../utility/getValidationSchema';
import { getFormikInitialValues } from '../utility/formatData';
import { useParams } from 'react-router-dom';
import { useUser } from '../../../hooks/useUser';
import { getCollectionPath } from '../../../utility/getPathFromParams';
import SimpleFormSection from './SimpleFormSection';
import UnsavedWarning from '../formElements/UnsavedWarning';
import classNames from '../../../utility/classNames';
import PageLoader from '../../loaders/PageLoader';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../../firebase/clientApp';
import SubmitButtons from './FormSubmitButtons';
import ClearButton from './ClearButton';
import { useSteps } from '../../../hooks/useSteps';
import FormUIOptions from '../formElements/formUIOptions';

export default function SimpleForm({
  formSchema,
  data,
  beforeData = {},
  docId,
  setSubmitted,
  embedVersion = false,
  onFormSubmitted = () => {},
}) {
  // For the BeforeAfter section, merge the existing data
  // from property and scenario to pre-populate inital input values

  const beforeAfterData = { ...beforeData, ...data };

  const { prev, next } = useSteps(beforeAfterData?.projectType || 'brrrr');

  const formName = formSchema.inputs.title
    .trim()
    .split(' ')
    .join('_')
    .toLowerCase();

  const sideBarSchema = formSchema.inputs.sideBarSchema;

  const { pathToSource } = formSchema;

  // Use dynamic params to create a path to the document location
  const params = useParams();
  const { user } = useUser();

  const collectionPath = getCollectionPath(pathToSource, params, user);

  const inputsSchema = formSchema.inputs.schema;

  // Generate a validation schema
  const flattenedSchema = inputsSchema.map((section) => section.schema);
  const reallyFlattenedSchema = flattenedSchema.flat().filter((field) => {
    // exclude non-input fields
    if (field.type === FormUIOptions.Header) return false;
    if (field.type === FormUIOptions.CalculatedSection) return false;

    // exclude fields that are not relevant to the current form version
    return embedVersion
      ? !field.excludeFromEmbeddedForm
      : !field.onlyInEmbeddedForm;
  });

  const validationSchema = getValidationSchema(reallyFlattenedSchema);

  // Generate an initial values object for Formik
  const formikInitialValues = getFormikInitialValues(
    reallyFlattenedSchema,
    beforeAfterData
  );

  const onSubmit = async (updates, setSubmitting, setTouched) => {
    setSubmitting(true);
    try {
      //if the form is the normal version (in app) then save the data to the database,
      //otherwise if they are using a form embedded into another site we save the data temporarily in local storage, to be used later
      if (!embedVersion) {
        await handleUpdate(updates, collectionPath, docId, formSchema, {
          ...beforeData,
          ...data,
        });
      } else {
        await handleUpdateEmbed(updates, 'visitors', formSchema, {
          ...beforeData,
          ...data,
        });
      }
      onFormSubmitted();
      setSubmitting(false);
      setSubmitted && setSubmitted(true);
      await setTouched({});
    } catch (error) {
      logEvent(analytics, 'exception', {
        firebase_screen: formName,
        content_type: 'update_form',
        description: error.code || error,
        fatal: false,
      });
      console.error(error);
      setSubmitting(false);
    }
  };

  // TBD add validation
  return (
    <Formik
      initialValues={formikInitialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={true}
      onSubmit={async (values, { setSubmitting, setTouched }) => {
        await onSubmit(values, setSubmitting, setTouched);
      }}
    >
      {({ isSubmitting, submitForm, isValid, errors, setValues }) => (
        <>
          {isSubmitting && <PageLoader />}
          <Form
            className={classNames(
              'w-full flex flex-col justify-stretch',
              isSubmitting && 'hidden',

              //this part can be used later to change the form layout depending on the form schema (for the sidebar to be added)
              sideBarSchema ? 'xl:grid xl:grid-cols-2-propertyform' : ''
            )}
          >
            <div className="w-full gap-6 flex flex-col justify-stretch">
              {inputsSchema.map((section) => (
                <SimpleFormSection
                  key={section.sectionValue}
                  section={section}
                  data={beforeAfterData}
                  beforeData={beforeData}
                  embedVersion={embedVersion}
                />
              ))}

              <SubmitButtons
                isSubmitting={isSubmitting}
                isValid={isValid}
                submitForm={submitForm}
                errors={errors}
                setValues={setValues}
                type={formSchema?.inputs?.submitButtons}
                embedVersion={embedVersion}
                prev={prev}
                next={next}
                label={formSchema.inputs?.submitButtonLabel}
                description={formSchema.inputs?.submitButtonDescription}
                onFormSubmitted={onFormSubmitted}
              />
              {!formSchema?.inputs?.doNotReset && !embedVersion && (
                <ClearButton type={formSchema?.inputs?.submitButtons} />
              )}
            </div>

            <UnsavedWarning
              submit={submitForm}
              submitting={isSubmitting}
              isValid={isValid}
            />
          </Form>
        </>
      )}
    </Formik>
  );
}
